import { useSyncExternalStore } from "react";
import { internVerkeer } from "./intern-verkeer";

/**
 * Cookie-/toestemmingsbeheer (AVG + ePrivacy). Eigen, custom implementatie — geen
 * externe CMP nodig. "Noodzakelijk" staat altijd aan; "Analytisch" (Google
 * Analytics) en "Marketing" (advertentie-/remarketingcookies) laden/activeren
 * pas ná expliciete toestemming via Google Consent Mode v2.
 *
 * Module-store + useSyncExternalStore (zelfde patroon als de btw-voorkeur), zodat
 * de banner, de footer-link en de cookiepagina dezelfde staat delen.
 */
export interface ConsentChoice {
  analytics: boolean;
  marketing: boolean;
  ts: number;
}

const KEY = "glastotaal.consent.v1";
// Versie ophogen bij een nieuwe categorie → bezoekers wordt opnieuw om
// toestemming gevraagd (je mag geen marketing-consent afleiden uit een oude keuze).
const VERSION = 2;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
  }
}

function read(): ConsentChoice | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { version?: number; analytics?: unknown; marketing?: unknown; ts?: unknown };
    if (v.version === VERSION && typeof v.analytics === "boolean" && typeof v.marketing === "boolean") {
      return { analytics: v.analytics, marketing: v.marketing, ts: typeof v.ts === "number" ? v.ts : 0 };
    }
  } catch {
    /* geen storage / corrupte waarde */
  }
  return null;
}

let current: ConsentChoice | null = read();
let settingsOpen = false;
const listeners = new Set<() => void>();
const emit = (): void => listeners.forEach((l) => l());
const subscribe = (l: () => void): (() => void) => {
  listeners.add(l);
  return () => { listeners.delete(l); };
};

// --- Tag-ID's (env-gedreven; leeg = no-op). -----------------------------------
// GA4 (analytisch) + Google Ads (marketing) lopen via dezelfde gtag.js en worden
// door Consent Mode gestuurd. Meta Pixel kent géén Consent Mode → hard gated op
// expliciete marketing-toestemming.
const gaId = (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined) || "";
const adsId = (import.meta.env.VITE_GOOGLE_ADS_ID as string | undefined) || "";
// Het Google-tag-ID (GT-…) van het Ads-account. Dit account is gemigreerd naar het
// Google-tag-systeem: googletagmanager.com serveert de Ads-bestemming alléén nog onder dit
// GT-ID — gtag/js?id=AW-… geeft een 404 (geverifieerd 01-08-2026). Configureren gebeurt dus
// op het GT-ID; conversies vuren met send_to "AW-…/label" en routeren naar dezelfde bestemming.
const googleTagId = (import.meta.env.VITE_GOOGLE_TAG_ID as string | undefined) || "";
const metaPixelId = (import.meta.env.VITE_META_PIXEL_ID as string | undefined) || "";
let gaBootstrapped = false;
let gaLoaded = false;
let metaLoaded = false;
// GA4 Gebruikers-ID van de ingelogde klant (Firebase-uid — gepseudonimiseerd, nooit een
// e-mailadres: Google verbiedt herleidbare gegevens in dit veld). Zie setAnalyticsUserId.
let analyticsUserId: string | undefined;

function bootstrapConsentMode(): void {
  if (gaBootstrapped || typeof window === "undefined") return;
  gaBootstrapped = true;
  window.dataLayer = window.dataLayer || [];
  // gtag moet het ruwe arguments-object pushen — een arrow met rest-args breekt GA.
  // Toewijzen kan gooien: sommige adblockers/extensies zetten hier een alleen-lezen
  // nep-gtag neer, en in een ES-module (strict mode) is dat een TypeError. Dat mag nooit
  // de bediening van de banner meeslepen.
  try {
    if (!window.gtag) window.gtag = function gtag() { window.dataLayer!.push(arguments); };
  } catch {
    /* gtag is afgeschermd — verderop staat overal een optional call. */
  }
  // Wereldwijde basis: toegestaan. Alleen binnen de EER/VK/Zwitserland (waar de AVG-achtige
  // opt-in-plicht geldt) overschrijven we dat hieronder naar geweigerd. Zonder deze regionale
  // splitsing wordt élke bezoeker — ook buiten de EU, waar geen opt-in vereist is — standaard
  // geweigerd; Google Tag Manager markeert dat terecht als een "Urgent" meetprobleem.
  window.gtag?.("consent", "default", {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "granted",
    wait_for_update: 500,
  });
  // In "advanced" modus stuurt GA in geweigerde staat alleen cookieloze, anonieme pings;
  // Google modelleert de rest.
  window.gtag?.("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    region: [
      "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IS",
      "IE", "IT", "LV", "LI", "LT", "LU", "MT", "NL", "NO", "PL", "PT", "RO", "SK", "SI",
      "ES", "SE", "GB", "CH",
    ],
    wait_for_update: 500,
  });
  // Behoud attributie zonder cookies + redigeer ad-identifiers zolang er geen
  // marketing-toestemming is (aanbevolen bij advanced consent mode).
  window.gtag?.("set", "url_passthrough", true);
  window.gtag?.("set", "ads_data_redaction", true);

  // js + config MOETEN vóór het eerste event in de dataLayer staan. gtag.js verwerkt de buffer op
  // volgorde; een event dat vóór de config staat heeft nog geen bestemming en wordt weggegooid.
  // Dat trof precies de eerste page_view van elk bezoek — de landingspagina — want die vuurt bij
  // de eerste render, terwijl loadGA() pas op idle draait. Gemeten 11-17 aug: landingspagina
  // "(not set)" bij 44 gebruikers tegenover 10 voor de best scorende échte pagina.
  // Deze aanroepen kosten niets: ze pushen alleen naar de dataLayer. Het gtag.js-script zelf
  // blijft vertraagd laden (whenIdleOrInteractive) — dáár zat de performancewinst, niet hier.
  window.gtag?.("js", new Date());
  // send_page_view:false — de SPA-routetracker (zie track.ts/trackPageView, aangeroepen bij elke
  // React Router-navigatie) is de enige bron van page_view-events. Zonder deze vlag vuurt gtag ZELF
  // ook nog een page_view bij het laden van dit script, met de URL die op dát moment toevallig
  // actief is (soms niet meer de landingspagina, want dit script laadt vertraagd — zie
  // whenIdleOrInteractive). Dat gaf dubbele/foute page_views. Nu is er precies één bron.
  if (gaId) {
    window.gtag?.("config", gaId, {
      anonymize_ip: true,
      send_page_view: false,
      // Eigen kijkverkeer (wij + testbezoeken) markeren; het GA4-gegevensfilter "Intern verkeer"
      // houdt het uit de rapporten. Zie intern-verkeer.ts — aanzetten met /?intern=1.
      ...(internVerkeer() ? { traffic_type: "internal" } : {}),
      // gtag.js laadt vertraagd (whenIdleOrInteractive). Was de klant al ingelogd vóórdat
      // het script er was, dan is `set` toen in de dataLayer-buffer beland zonder dat GA4
      // erbij kon; daarom hier nogmaals meegeven.
      ...(analyticsUserId ? { user_id: analyticsUserId } : {}),
    });
  }
  // Google Ads rechtstreeks op dezelfde gtag.js (01-08-2026, verving de GTM-container):
  // GTM + een tweede volledige Google-tag-load waren samen ~370ms extra main-thread-script
  // voor functionaliteit die gtag zelf al aan boord heeft — remarketing-config, Conversion
  // Linker (gclid→cookie) én Enhanced Conversions. De conversie-events zelf vuren nu
  // direct in track.ts met send_to-labels. Consent Mode gate't dit identiek.
  // Config op het GT-ID (zie googleTagId hierboven); terugval op het AW-ID voor het geval
  // het GT-ID ooit uit .env verdwijnt.
  const adsConfigId = googleTagId || adsId;
  if (adsConfigId) window.gtag?.("config", adsConfigId, { allow_enhanced_conversions: true });
}

function loadGA(): void {
  // gtag.js dekt zowel GA4 als Google Ads; minstens één ID nodig.
  if (gaLoaded || (!gaId && !adsId) || typeof document === "undefined") return;
  gaLoaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId || adsId}`;
  document.head.appendChild(s);
}

/** Meta Pixel — hard gated: laadt pas ná marketing-toestemming (geen Consent Mode). */
function loadMeta(): void {
  if (metaLoaded || !metaPixelId || typeof document === "undefined") return;
  metaLoaded = true;
  // Standaard Meta Pixel-bootstrap (queue tot fbevents.js geladen is).
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const w = window as any;
  const n: any = (w.fbq = function fbq(...args: unknown[]) {
    n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
  });
  if (!w._fbq) w._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];
  /* eslint-enable @typescript-eslint/no-explicit-any */
  const t = document.createElement("script");
  t.async = true;
  t.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(t);
  window.fbq?.("init", metaPixelId);
  window.fbq?.("track", "PageView");
}

function apply(choice: ConsentChoice): void {
  bootstrapConsentMode();
  const m = choice.marketing ? "granted" : "denied";
  // Google (GA4 + Ads): Consent Mode stuurt cookieloos/redacted bij denied.
  window.gtag?.("consent", "update", {
    analytics_storage: choice.analytics ? "granted" : "denied",
    ad_storage: m,
    ad_user_data: m,
    ad_personalization: m,
  });
  // Meta Pixel: laadt alleen ná marketing-toestemming; bij intrekken stilzetten.
  if (choice.marketing) loadMeta();
  else if (metaLoaded) window.fbq?.("consent", "revoke");
}

// GTM/gtag zijn ~1,2s aan main-thread-tijd (parse + execute) die niets bijdraagt aan de
// eerste render (zelfde soort kost als de nu-uitgestelde Firestore-reads, zie firebaseIdle()
// in lib/firebase/app.ts). We wachten daarom op idle óf de eerste echte interactie
// (wat eerder komt), zodat de tags niet meeconcurreren met LCP/hydratie. De dataLayer/
// gtag-stub (hierboven) buffert intussen gewoon door — er gaat geen event verloren,
// het wordt alleen een fractie later verwerkt zodra het echte script laadt.
function whenIdleOrInteractive(cb: () => void): void {
  if (typeof window === "undefined") return;
  let done = false;
  const events = ["pointerdown", "keydown", "scroll", "touchstart"] as const;
  const run = (): void => {
    if (done) return;
    done = true;
    events.forEach((e) => window.removeEventListener(e, run));
    cb();
  };
  events.forEach((e) => window.addEventListener(e, run, { passive: true, once: true }));
  const ric = (window as unknown as {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  }).requestIdleCallback;
  if (ric) ric(run, { timeout: 3000 });
  else setTimeout(run, 2000);
}

/**
 * Consent-standaarden, js + config en een eerder opgeslagen keuze in de dataLayer zetten — één keer,
 * synchroon, zonder netwerkkost. track.ts roept dit aan vóór élk event.
 *
 * Waarom niet alleen vanuit initConsent: die draait in de CookieBanner, en die zit in de (lazy
 * geladen) SiteLayout. De page_view van de landingspagina vuurt in App al bij de eerste commit,
 * vóórdat die layout er is. window.gtag bestond dan nog niet, het event verdween stil — en omdat
 * trackPageView een route maar één keer meldt, kwam hij nooit meer. Gevolg (21 aug–17 sep 2026):
 * landingspagina "(not set)" bij 42% van de sessies, ook voor bezoekers die al akkoord hadden gegeven.
 */
let tagsPrepared = false;
export function prepareTags(): void {
  if (tagsPrepared) return;
  tagsPrepared = true;
  // Eén blok: valt het laden van een tag om (adblocker, netwerk), dan draait de site gewoon
  // door — meten is nooit belangrijker dan werken.
  try {
    bootstrapConsentMode();
    if (current) apply(current);
  } catch (err) {
    console.warn("[consent] initialiseren van de tags mislukt", err);
  }
}

/**
 * App-start (advanced consent mode): default op denied zetten en een eerdere keuze
 * toepassen (synchroon — zet alleen de gtag-stub/dataLayer, geen netwerkkost). GA4/GTM
 * zelf laden pas ná idle of de eerste interactie. Zonder VITE_GA_MEASUREMENT_ID gebeurt
 * er niets.
 */
export function initConsent(): void {
  prepareTags();
  whenIdleOrInteractive(() => {
    try {
      loadGA();
    } catch (err) {
      console.warn("[consent] laden van GA/Ads mislukt", err);
    }
  });
}

/**
 * GA4 **Gebruikers-ID** koppelen aan de ingelogde klant (of wissen bij uitloggen).
 *
 * Waarom: zonder dit telt GA4 per browser. Dezelfde klant die eerst op zijn telefoon kijkt en
 * daarna op de laptop bestelt, is dan twee "gebruikers" en twee losse trajecten — en de aankoop
 * lijkt uit het niets te komen. Met een gebruikers-ID plakt GA4 die sessies aan elkaar, mits in
 * Beheer → Identiteit voor rapportage "Gemengd" of "Waargenomen" staat.
 *
 * Wat we sturen: de **Firebase-uid**. Dat is een ondoorzichtige sleutel die alleen wij kunnen
 * terugleiden naar een persoon. Een e-mailadres (of een hash daarvan) is hier VERBODEN — Google
 * weigert herleidbare gegevens in dit veld en dat kost je de property.
 *
 * De aanroep is veilig vóórdat gtag.js geladen is: de stub buffert 'm in de dataLayer, en loadGA()
 * geeft het ID bovendien nog eens mee in de config-aanroep.
 */
export function setAnalyticsUserId(uid?: string | null): void {
  const nieuw = (uid ?? "").trim() || undefined;
  if (nieuw === analyticsUserId) return;
  analyticsUserId = nieuw;
  // null (niet undefined) is wat GA4 wil om een eerder gezet ID te wissen bij uitloggen.
  try {
    window.gtag?.("set", { user_id: nieuw ?? null });
  } catch {
    /* gtag afwezig/geblokkeerd → meten is optioneel, dit mag nooit iets breken */
  }
}

/** Het nu gekoppelde GA4-gebruikers-ID (voor tests en voor de order-payload). */
export function analyticsUserIdSnapshot(): string | undefined {
  return analyticsUserId;
}

/**
 * Forceert het gtag.js-script nu meteen, zonder op idle/interactie te wachten.
 *
 * Waarom dit nodig is: gaEvent() (track.ts) pusht altijd naar de dataLayer-buffer, ook vóórdat
 * gtag.js geladen is — dat lijkt veilig, maar de buffer is alleen geheugen. Verlaat de bezoeker
 * de pagina (tab sluiten, terugnavigeren) vóórdat gtag.js daadwerkelijk geladen is, dan wordt die
 * buffer nooit verzonden en is het event stil verloren. Op de meeste pagina's is dat een
 * verwaarloosbaar risico (mensen scrollen/klikken binnen enkele seconden, wat het laden alsnog
 * triggert). Maar op de bedankpagina na Mollie — een volledige, harde redirect terug naar de
 * site, dus de hele app (en dus ook deze module) start daar helemaal opnieuw op — is dat risico
 * juist het grootst: de klant heeft zijn taak voltooid en sluit de tab vaak meteen. Precies dáár
 * gaat het conversie-event verloren dat het meest telt (purchase + Ads-conversie).
 *
 * Daarom roept trackPurchase() en trackLead() (track.ts) dit expliciet aan: die twee events zijn
 * zeldzaam maar hoogwaardig, dus de kleine main-thread-kost van meteen laden weegt niet op tegen
 * het risico om ze kwijt te raken. Reguliere events (page_view, add_to_cart, etc.) blijven op de
 * vertraagde/idle-route — daar is performance de betere afweging.
 */
export function ensureAnalyticsLoaded(): void {
  try {
    loadGA();
  } catch (err) {
    console.warn("[consent] direct laden van GA/Ads mislukt", err);
  }
}

/** Toestemming opslaan (analytics + marketing aan/uit) en direct toepassen. Sluit het paneel. */
export function setConsent(analytics: boolean, marketing: boolean): void {
  const hadAnalytics = current?.analytics === true;
  const choice: ConsentChoice = { analytics, marketing, ts: Date.now() };
  current = choice;
  try {
    localStorage.setItem(KEY, JSON.stringify({ version: VERSION, ...choice }));
  } catch {
    /* geen storage */
  }
  // Eerst het scherm bijwerken, dán pas de tags. Andersom bleef de banner staan zodra een
  // adblocker of extensie een van de tracking-aanroepen liet gooien: de keuze was opgeslagen,
  // maar `emit()` werd nooit bereikt en de knop leek niets te doen.
  settingsOpen = false;
  emit();
  try {
    apply(choice);
    if (analytics && !hadAnalytics) resendLandingPageView();
  } catch (err) {
    console.warn("[consent] toestemming toepassen op de tags mislukt", err);
  }
}

/**
 * Een nieuwe bezoeker landt met analytics "denied": de eerste page_view gaat dan als cookieloze
 * ping weg, die GA4 aan geen enkele sessie koppelt. Klikt hij daarna op Accepteren, dan begint GA4
 * vanaf dát moment een echte sessie — zonder page_view, dus met landingspagina "(not set)".
 * Gemeten 21 aug–17 sep 2026: 279 van 657 sessies (42%), en maar 252 van 477 gebruikers hadden
 * überhaupt een page_view. Daarom de huidige pagina opnieuw melden zodra de toestemming er komt.
 * Geen dubbeltelling: de cookieloze ping telt in de standaardrapporten niet mee.
 */
function resendLandingPageView(): void {
  if (typeof window === "undefined" || !window.location) return;
  const path = `${window.location.pathname ?? ""}${window.location.search ?? ""}`;
  if (path.startsWith("/admin")) return;
  window.gtag?.("event", "page_view", {
    page_location: `${window.location.origin}${path}`,
    page_path: path,
    ...(typeof document !== "undefined" && document.title ? { page_title: document.title } : {}),
  });
}

export function openConsentSettings(): void { settingsOpen = true; emit(); }
export function closeConsentSettings(): void { settingsOpen = false; emit(); }

/**
 * De huidige keuze buiten React om (null = nog niet gekozen). Voor code die géén hook kan
 * gebruiken maar wél moet weten of marketing-toestemming er is — bijvoorbeeld het vastleggen
 * van een Google-klik-ID.
 */
export function consentSnapshot(): ConsentChoice | null {
  return current;
}

/** Huidige keuze (null = nog niet gekozen → toon de banner). */
export function useConsent(): ConsentChoice | null {
  return useSyncExternalStore(subscribe, () => current, () => current);
}

/** Of het instellingen-paneel open moet (footer + cookiepagina kunnen 'm openen). */
export function useConsentSettingsOpen(): boolean {
  return useSyncExternalStore(subscribe, () => settingsOpen, () => settingsOpen);
}
